/**
 * Captures a design as a PNG Blob using Canvas 2D.
 * All remote images are fetched through /media/proxy to avoid canvas CORS taint.
 */

const API_BASE = process.env.REACT_APP_API_URL || "";

function getAuthHeaders() {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function loadImgElement(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load: ${src.slice(0, 80)}`));
        img.src = src;
    });
}

async function loadImageSafe(src) {
    if (!src) return null;
    if (src.startsWith("blob:") || src.startsWith("data:")) {
        return loadImgElement(src);
    }
    const proxyUrl = `${API_BASE}/media/proxy?url=${encodeURIComponent(src)}`;
    const res = await fetch(proxyUrl, { headers: getAuthHeaders() });
    if (!res.ok) {
        console.warn(`[captureDesign] Proxy failed ${res.status} for:`, src.slice(0, 120));
        throw new Error(`Proxy HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
        return await loadImgElement(blobUrl);
    } finally {
        URL.revokeObjectURL(blobUrl);
    }
}

function drawRoundRect(ctx, x, y, w, h, r) {
    r = Math.min(Math.abs(r || 0), w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

export async function captureDesignToBlob(design) {
    const { canvasSize, canvasBg, elements } = design || {};
    if (!canvasSize?.width || !canvasSize?.height) return null;

    const sorted = [...(elements || [])].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));

    // Pre-load ALL images in parallel before drawing
    const imageCache = new Map();
    await Promise.all(
        sorted
            .filter((el) => el.type === "image" && el.src)
            .map(async (el) => {
                try {
                    const img = await loadImageSafe(el.src);
                    if (img) imageCache.set(el.id, img);
                } catch (err) {
                    console.warn(`[captureDesign] Image not loaded (el ${el.id}):`, err.message);
                }
            })
    );

    const canvas = document.createElement("canvas");
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = canvasBg || "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    for (const el of sorted) {
        ctx.save();
        ctx.globalAlpha = el.opacity ?? 1;

        if (el.type === "rect") {
            if (!el.noFill) {
                ctx.fillStyle = el.fill || "transparent";
                drawRoundRect(ctx, el.x, el.y, el.width, el.height, el.borderRadius || 0);
                ctx.fill();
            }
            if (!el.noBorder && (el.strokeWidth || 0) > 0) {
                ctx.strokeStyle = el.stroke || "#000";
                ctx.lineWidth = el.strokeWidth;
                drawRoundRect(ctx, el.x, el.y, el.width, el.height, el.borderRadius || 0);
                ctx.stroke();
            }
        }

        if (el.type === "circle") {
            const cx = el.x + el.width / 2;
            const cy = el.y + el.height / 2;
            ctx.beginPath();
            ctx.ellipse(cx, cy, el.width / 2, el.height / 2, 0, 0, 2 * Math.PI);
            if (!el.noFill) { ctx.fillStyle = el.fill || "transparent"; ctx.fill(); }
            if (!el.noBorder && (el.strokeWidth || 0) > 0) {
                ctx.strokeStyle = el.stroke || "#000";
                ctx.lineWidth = el.strokeWidth;
                ctx.stroke();
            }
        }

        if (el.type === "text") {
            ctx.globalAlpha = 1;
            ctx.fillStyle = el.color || "#000";
            const size = Number(el.fontSize) || 16;
            ctx.font = `${el.fontStyle || "normal"} ${el.fontWeight || "normal"} ${size}px ${el.fontFamily || "sans-serif"}`;
            const align = el.textAlign || el.align || "left";
            ctx.textAlign = align;

            // Use "alphabetic" baseline (CSS default) and derive the y of the
            // first baseline from actual font metrics to match CSS rendering.
            //   CSS first baseline = el.y + halfLeading + fontAscender
            //   halfLeading = (lineHeight - fontSize) / 2
            ctx.textBaseline = "alphabetic";
            const lh = size * 1.2;
            const halfLeading = (lh - size) / 2;
            const metrics = ctx.measureText("|");
            const fontAscender = (metrics.fontBoundingBoxAscent !== undefined && metrics.fontBoundingBoxAscent > 0)
                ? metrics.fontBoundingBoxAscent
                : size * 0.8;
            const firstBaselineY = el.y + halfLeading + fontAscender;

            ctx.save();
            ctx.beginPath();
            ctx.rect(el.x, el.y, el.width, el.height);
            ctx.clip();

            let tx = el.x;
            if (align === "center") tx = el.x + el.width / 2;
            if (align === "right") tx = el.x + el.width;

            // Reproduce CSS white-space: pre-wrap word wrapping
            const allLines = [];
            for (const paragraph of (el.text || "").split("\n")) {
                if (paragraph === "") { allLines.push(""); continue; }
                const words = paragraph.split(" ");
                let currentLine = "";
                for (const word of words) {
                    const test = currentLine ? `${currentLine} ${word}` : word;
                    if (ctx.measureText(test).width > el.width && currentLine) {
                        allLines.push(currentLine);
                        currentLine = word;
                    } else {
                        currentLine = test;
                    }
                }
                if (currentLine) allLines.push(currentLine);
            }

            allLines.forEach((line, i) => ctx.fillText(line, tx, firstBaselineY + i * lh));
            ctx.restore();
        }

        if (el.type === "image") {
            const img = imageCache.get(el.id);
            if (img) {
                ctx.save();
                ctx.beginPath();
                ctx.rect(el.x, el.y, el.width, el.height);
                ctx.clip();

                // Reproduce CSS object-fit: cover
                // Scale so the image covers the element box, then center
                const scale = Math.max(el.width / img.naturalWidth, el.height / img.naturalHeight);
                const scaledW = img.naturalWidth * scale;
                const scaledH = img.naturalHeight * scale;
                const dx = el.x + (el.width - scaledW) / 2;
                const dy = el.y + (el.height - scaledH) / 2;
                ctx.drawImage(img, dx, dy, scaledW, scaledH);

                ctx.restore();
            }
        }

        ctx.restore();
    }

    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}
